import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authAPI, authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, Loader2, LogOut, User, Mail, Shield, Key, Eye, EyeOff, CheckCircle2, Building2, Phone, Pencil, Lock } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function Account() {
  const { user, logout, refreshUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [referralOther, setReferralOther] = useState('');
  const [defaultLockboxCode, setDefaultLockboxCode] = useState('');
  
  // Change password form state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Initialize profile fields from user
  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setCompany(user.company || '');
      setPhone(user.phone || '');
      setReferralSource(user.referral_source || '');
      setReferralOther(user.referral_other || '');
      setDefaultLockboxCode(user.default_lockbox_code || '');
    }
  }, [user]);

  // Check for pending profile data from registration
  useEffect(() => {
    const pendingProfile = localStorage.getItem('rc_pending_profile');
    if (pendingProfile && user) {
      const data = JSON.parse(pendingProfile);
      if (data.applied) { localStorage.removeItem('rc_pending_profile'); return; }
      // Update profile with pending data
      updateProfile(data);
      localStorage.removeItem('rc_pending_profile');
    }
  }, [user]);

  // Password validation
  const passwordRequirements = [
    { met: newPassword.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(newPassword), text: 'One uppercase letter' },
    { met: /[a-z]/.test(newPassword), text: 'One lowercase letter' },
    { met: /\d/.test(newPassword), text: 'One number' },
  ];
  
  const allRequirementsMet = passwordRequirements.every(r => r.met);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleLogout = async () => {
    navigate('/', { replace: true });
    await logout();
  };

  const updateProfile = async (data) => {
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess(false);
    
    try {
      const response = await authFetch(`${API_BASE}/api/auth/me/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update profile');
      }
      
      await refreshUser();
      setProfileSuccess(true);
      setIsEditingProfile(false);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError(err.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    await updateProfile({
      full_name: fullName.trim() || null,
      company: company.trim() || null,
      phone: phone.trim() || null,
      referral_source: referralSource || null,
      referral_other: referralSource === 'other' ? referralOther.trim() : null,
      default_lockbox_code: defaultLockboxCode.trim() || null,
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (!allRequirementsMet) {
      setPasswordError('Please meet all password requirements');
      return;
    }

    if (!passwordsMatch) {
      setPasswordError('New passwords do not match');
      return;
    }

    setPasswordLoading(true);

    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowChangePassword(false);
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <h1 className="text-3xl font-light text-white tracking-wide">My Account</h1>
          <p className="text-[#E8E4DE]/60 mt-2">Manage your RC Studios account settings</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Profile Card */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#C6A14A]/20 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-[#C6A14A]" />
                </div>
                <div>
                  <CardTitle className="text-xl text-white">Profile Information</CardTitle>
                  <CardDescription className="text-[#E8E4DE]/60">Your account details</CardDescription>
                </div>
              </div>
              {!isEditingProfile && (
                <Button
                  onClick={() => setIsEditingProfile(true)}
                  variant="ghost"
                  size="sm"
                  className="text-[#C6A14A] hover:text-[#D4AF5A] hover:bg-[#C6A14A]/10"
                  data-testid="edit-profile-btn"
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {profileSuccess && (
              <div className="flex items-center gap-2 p-3 mb-4 text-sm text-green-400 bg-green-400/10 border border-green-400/20 rounded-lg">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>Profile updated successfully!</span>
              </div>
            )}

            {isEditingProfile ? (
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                {profileError && (
                  <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{profileError}</span>
                  </div>
                )}
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-[#E8E4DE]/80 text-sm">Full Name</Label>
                    <Input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Your full name"
                      className="bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A]"
                      data-testid="profile-fullname-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#E8E4DE]/80 text-sm">Company / Brokerage</Label>
                    <Input
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="e.g., RE/MAX, Keller Williams"
                      className="bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A]"
                      data-testid="profile-company-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80 text-sm">Phone Number</Label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A]"
                    data-testid="profile-phone-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80 text-sm">Default Lockbox Code</Label>
                  <Input
                    value={defaultLockboxCode}
                    onChange={(e) => setDefaultLockboxCode(e.target.value)}
                    placeholder="e.g., 1234"
                    maxLength={20}
                    className="bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A]"
                    data-testid="profile-lockbox-input"
                  />
                  <p className="text-xs text-[#E8E4DE]/50">Pre-fills your lockbox code on real estate orders</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80 text-sm">How did you hear about us?</Label>
                  <Select value={referralSource} onValueChange={setReferralSource}>
                    <SelectTrigger className="bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A]">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1724] border-white/10">
                      <SelectItem value="google" className="text-white hover:bg-white/10">Google Search</SelectItem>
                      <SelectItem value="facebook" className="text-white hover:bg-white/10">Facebook</SelectItem>
                      <SelectItem value="instagram" className="text-white hover:bg-white/10">Instagram</SelectItem>
                      <SelectItem value="word_of_mouth" className="text-white hover:bg-white/10">Word of Mouth</SelectItem>
                      <SelectItem value="other" className="text-white hover:bg-white/10">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {referralSource === 'other' && (
                  <div className="space-y-2">
                    <Label className="text-[#E8E4DE]/80 text-sm">Please specify</Label>
                    <Input
                      value={referralOther}
                      onChange={(e) => setReferralOther(e.target.value)}
                      placeholder="How did you find us?"
                      className="bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A]"
                    />
                  </div>
                )}
                
                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={profileLoading}
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]"
                    data-testid="save-profile-btn"
                  >
                    {profileLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditingProfile(false);
                      setProfileError('');
                      // Reset to original values
                      setFullName(user?.full_name || '');
                      setCompany(user?.company || '');
                      setPhone(user?.phone || '');
                      setReferralSource(user?.referral_source || '');
                      setReferralOther(user?.referral_other || '');
                      setDefaultLockboxCode(user?.default_lockbox_code || '');
                    }}
                    className="border-white/10 text-[#E8E4DE] hover:bg-white/5"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-[#050816]/50 rounded-lg">
                  <Mail className="h-5 w-5 text-[#C6A14A]" />
                  <div>
                    <p className="text-sm text-[#E8E4DE]/60">Email</p>
                    <p className="text-white" data-testid="account-email">{user?.email}</p>
                  </div>
                  {user?.email_verified && (
                    <span className="ml-auto text-xs bg-green-400/20 text-green-400 px-2 py-1 rounded">
                      Verified
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3 p-4 bg-[#050816]/50 rounded-lg">
                  <User className="h-5 w-5 text-[#C6A14A]" />
                  <div>
                    <p className="text-sm text-[#E8E4DE]/60">Full Name</p>
                    <p className="text-white">{user?.full_name || <span className="text-white/40">Not set</span>}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-[#050816]/50 rounded-lg">
                  <Building2 className="h-5 w-5 text-[#C6A14A]" />
                  <div>
                    <p className="text-sm text-[#E8E4DE]/60">Company / Brokerage</p>
                    <p className="text-white">{user?.company || <span className="text-white/40">Not set</span>}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-[#050816]/50 rounded-lg">
                  <Phone className="h-5 w-5 text-[#C6A14A]" />
                  <div>
                    <p className="text-sm text-[#E8E4DE]/60">Phone</p>
                    <p className="text-white">{user?.phone || <span className="text-white/40">Not set</span>}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-[#050816]/50 rounded-lg">
                  <Lock className="h-5 w-5 text-[#C6A14A]" />
                  <div>
                    <p className="text-sm text-[#E8E4DE]/60">Default Lockbox Code</p>
                    <p className="text-white">{user?.default_lockbox_code || <span className="text-white/40">Not set</span>}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-[#050816]/50 rounded-lg">
                  <Shield className="h-5 w-5 text-[#C6A14A]" />
                  <div>
                    <p className="text-sm text-[#E8E4DE]/60">Account Type</p>
                    <p className="text-white capitalize">
                      {isAdmin ? 'Administrator' : 'Standard User'}
                    </p>
                  </div>
                  {isAdmin && (
                    <span className="ml-auto text-xs bg-[#C6A14A]/20 text-[#C6A14A] px-2 py-1 rounded">
                      Admin
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Security Card */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#C6A14A]/20 rounded-full flex items-center justify-center">
                <Key className="h-6 w-6 text-[#C6A14A]" />
              </div>
              <div>
                <CardTitle className="text-xl text-white">Security</CardTitle>
                <CardDescription className="text-[#E8E4DE]/60">Manage your password</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {passwordSuccess && (
              <div className="flex items-center gap-2 p-3 mb-4 text-sm text-green-400 bg-green-400/10 border border-green-400/20 rounded-lg">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>Password changed successfully!</span>
              </div>
            )}

            {!showChangePassword ? (
              <Button
                onClick={() => setShowChangePassword(true)}
                variant="outline"
                className="border-white/10 text-[#E8E4DE] hover:bg-white/5"
                data-testid="change-password-btn"
              >
                Change Password
              </Button>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4">
                {passwordError && (
                  <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{passwordError}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80 text-sm">Current Password</Label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A]"
                    data-testid="current-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80 text-sm">New Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A] pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {newPassword.length > 0 && (
                    <div className="space-y-1 pt-2">
                      {passwordRequirements.map((req, i) => (
                        <div 
                          key={i} 
                          className={`flex items-center gap-2 text-xs ${
                            req.met ? 'text-green-400' : 'text-white/40'
                          }`}
                        >
                          <div className={`w-1.5 h-1.5 rounded-full ${
                            req.met ? 'bg-green-400' : 'bg-white/20'
                          }`} />
                          {req.text}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-[#E8E4DE]/80 text-sm">Confirm New Password</Label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={`bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A] ${
                      confirmPassword.length > 0 && !passwordsMatch ? 'border-red-400/50' : ''
                    }`}
                    data-testid="confirm-new-password-input"
                  />
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className="text-xs text-red-400">Passwords do not match</p>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={passwordLoading || !allRequirementsMet || !passwordsMatch}
                    className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] disabled:opacity-50"
                    data-testid="submit-password-change-btn"
                  >
                    {passwordLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowChangePassword(false);
                      setPasswordError('');
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="border-white/10 text-[#E8E4DE] hover:bg-white/5"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Quick Links Card */}
        <Card className="bg-[#0f1724] border-white/10">
          <CardHeader>
            <CardTitle className="text-xl text-white">Quick Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              asChild
              variant="outline"
              className="w-full justify-start border-white/10 text-[#E8E4DE] hover:bg-white/5 hover:text-[#C6A14A]"
            >
              <Link to="/orders">
                My Orders
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="w-full justify-start border-white/10 text-[#E8E4DE] hover:bg-white/5 hover:text-[#C6A14A]"
            >
              <Link to="/book">
                Book a Real Estate Shoot
              </Link>
            </Button>
            {isAdmin && (
              <Button
                asChild
                variant="outline"
                className="w-full justify-start border-[#C6A14A]/30 text-[#C6A14A] hover:bg-[#C6A14A]/10"
              >
                <Link to="/admin">
                  Admin Dashboard
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Logout */}
        <Separator className="bg-white/10" />
        
        <div className="flex justify-end">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-red-400/30 text-red-400 hover:bg-red-400/10 hover:text-red-300"
            data-testid="logout-btn"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
}
